/* ============================================================
   FitLibrary — fitlibrary-cart.js
   THE single Cart interaction controller. Shopify is always the
   authority: every quantity change, removal and clear goes to the
   Ajax Cart API and the section is re-rendered from what Shopify
   returns. Nothing is ever computed in the browser and shown as a
   price, and no cart state is kept in JS.

   Architecture notes
   - ONE delegated listener set per section root, bound once. The
     root element itself is never replaced (only its innerHTML), so
     re-rendering can never produce duplicate handlers.
   - Requests are SERIALISED through a promise chain, so hammering
     the +/- buttons queues instead of racing.
   - Lines are addressed by their line-item KEY, not by index.
     Indices shift the moment a line is removed; keys do not.

   2026-08-18 · STEP 04 — SMART CART lives in THIS controller.
   There is no second cart engine and no second mutation path. Smart
   Cart is pure guidance: it reads the composition Shopify already
   rendered, shows at most ONE card, and never changes the cart. Only
   the customer can act.

   Relationship identity comes from data attributes that Liquid
   stamped from TYPED product references — never from a title or a
   handle. A line the merchant has not configured carries no kind, so
   it is simply ignored rather than guessed at.

   State that must survive a section re-render (dismissal, review
   toggle) lives in this closure. The DOM is re-queried on every
   evaluation because innerHTML is replaced under us.

   2026-08-18 · STEP 04.1 — two hardening fixes.
   (1) Bundle overlap is now attributed PER LINE. Unioning coverage
       and then naming bundles[0] could tell a customer that their
       Printable Bundle contains an eBook, which is false. Each line
       now records which present bundle types actually cover it, and
       the copy names only those.
   (2) A section re-render now re-syncs the first-order presentation
       as well as Smart Cart, so fresh Liquid cannot restore the
       AVAILABLE pane while the attempt marker is still in the URL.
   ============================================================ */
(function () {
  "use strict";
  var FL = (window.FitLibrary = window.FitLibrary || {});
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";

  /* Which individual kinds each bundle type already contains. Driven by
     bundle type, so the Printable Bundle can never be treated as covering
     eBooks. Bundle lines are never flagged against each other. */
  var BUNDLE_COVERS = { ebooks: ["ebook", "pack"], printables: ["pack"] };

  /* ---- helpers ----------------------------------------------------- */

  function sectionId(root) {
    return root.getAttribute("data-fl-cart-section") || "";
  }

  function busy(root, state) {
    root.classList.toggle("is-busy", !!state);
    root.setAttribute("aria-busy", state ? "true" : "false");
    root.querySelectorAll("[data-fl-qty-step],[data-fl-qty],[data-fl-remove],[data-fl-clear]")
      .forEach(function (el) { el.disabled = !!state; });
  }

  function showError(root, message) {
    var box = root.querySelector("[data-fl-cart-error]");
    if (!box) return;
    box.textContent = message || "";
    box.hidden = !message;
    if (message) FL.announce(message);
  }

  /* Consent lives only in the DOM, so it is carried across a section
     re-render by hand. Without this, changing a quantity would silently
     un-tick the consent box. */
  function snapshot(root) {
    var consent = root.querySelector("[data-fl-consent-input]");
    return { consent: consent ? consent.checked : false };
  }

  function restore(root, snap) {
    if (!snap) return;
    var consent = root.querySelector("[data-fl-consent-input]");
    if (consent) consent.checked = snap.consent;
  }

  /* ---- consent gate ------------------------------------------------ */

  function syncConsent(root) {
    var input = root.querySelector("[data-fl-consent-input]");
    if (!input) return;
    var ok = input.checked;
    var label = root.querySelector("[data-fl-consent]");
    var checkout = root.querySelector("[data-fl-checkout]");
    var hint = root.querySelector("[data-fl-consent-hint]");
    if (label) label.classList.toggle("is-checked", ok);
    if (checkout) checkout.disabled = !ok;
    if (hint) hint.style.opacity = ok ? "0" : "1";
  }

  /* ---- first order offer ------------------------------------------- */
  /* The APPLIED state is rendered by Liquid from real Shopify allocations —
     never decided here. The only thing this does is reveal the neutral
     "couldn't apply" pane when the visitor has come back from an attempt and
     Shopify granted nothing. The marker is evidence of an ATTEMPT only; it is
     never treated as proof about the customer's history.

     The state guard below is what makes this safe to re-run after every
     section re-render: it returns immediately unless Liquid rendered
     AVAILABLE, so it can never override applied, bundleonly or none. */
  function syncOffer(root) {
    var offer = root.querySelector("[data-fl-offer]");
    if (!offer) return;
    if (offer.getAttribute("data-fl-offer-state") !== "available") return;
    if (window.location.search.indexOf("fl_offer=attempted") === -1) return;

    var avail = offer.querySelector('[data-fl-offer-pane="available"]');
    var att = offer.querySelector('[data-fl-offer-pane="attempted"]');
    if (avail) avail.hidden = true;
    if (att) att.hidden = false;
  }

  /* ---- section re-render ------------------------------------------- */

  function applySection(root, html) {
    var doc = new DOMParser().parseFromString(html, "text/html");
    var fresh = doc.querySelector("[data-fl-cart-section]");
    if (!fresh) return;
    var snap = snapshot(root);
    root.className = fresh.className;
    root.innerHTML = fresh.innerHTML;
    restore(root, snap);
    syncConsent(root);
    document.dispatchEvent(new CustomEvent("fitlibrary:cart:updated"));
  }

  function refresh(root, cart) {
    var id = sectionId(root);
    if (cart && cart.sections && id && cart.sections[id]) {
      applySection(root, cart.sections[id]);
      return Promise.resolve(cart);
    }
    var sep = window.location.search ? window.location.search + "&" : "?";
    var url = window.location.pathname + sep + "section_id=" + encodeURIComponent(id);
    return fetch(url, { headers: { Accept: "text/html" } })
      .then(function (r) { return r.text(); })
      .then(function (html) { applySection(root, html); return cart; });
  }

  /* ---- the one mutation path --------------------------------------- */

  function request(root, path, payload, announcement) {
    busy(root, true);
    showError(root, "");
    var body = payload || {};
    body.sections = sectionId(root);
    body.sections_url = window.location.pathname;
    return fetch(ROOT + path, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body)
    })
      .then(function (r) {
        return r.json().then(function (data) {
          if (!r.ok) throw data || new Error("cart");
          return data;
        });
      })
      .then(function (cart) {
        if (typeof cart.item_count === "number") FL.setCartCount(cart.item_count);
        if (announcement) FL.announce(announcement);
        return refresh(root, cart);
      })
      .then(function (cart) { busy(root, false); return cart; })
      .catch(function (err) {
        busy(root, false);
        var message = (err && (err.description || err.message)) || "";
        showError(root, message || "We could not update your cart. Please try again.");
        /* Shopify refused the change, so the DOM may now disagree with the
           real cart. Pull the truth back in rather than leaving a stale
           quantity on screen. */
        return refresh(root, null).catch(function () { return null; });
      });
  }

  /* ---- init -------------------------------------------------------- */

  function init(root) {
    if (!root || root.dataset.flCartBound === "true") return;
    root.dataset.flCartBound = "true";

    /* Serialise every mutation so rapid clicks queue instead of racing. */
    var chain = Promise.resolve();
    function enqueue(fn) {
      chain = chain.then(fn, fn);
      return chain;
    }

    /* ---- Smart Cart state (closure — survives section re-render) ---- */
    var smartDismissSig = null;   /* in-memory only. No storage, no cookies. */
    var reviewActive = false;
    var smartCurrent = { state: "none", items: [] };

    function titleCase(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : ""; }

    function unique(arr) {
      return arr.filter(function (v, i, a) { return a.indexOf(v) === i; });
    }

    function humanList(parts) {
      if (parts.length < 2) return parts[0] || "";
      return parts.slice(0, -1).join(", ") + " and " + parts[parts.length - 1];
    }

    /* Read the composition Shopify just rendered. Identity comes only from
       the typed-reference attributes; an unconfigured line has no kind and
       is therefore invisible to every detector below. */
    function readLines() {
      return Array.prototype.map.call(root.querySelectorAll("[data-fl-key]"), function (el) {
        return {
          el: el,
          key: el.getAttribute("data-fl-key") || "",
          kind: el.getAttribute("data-fl-kind") || "",
          family: el.getAttribute("data-fl-family") || "",
          bundleType: el.getAttribute("data-fl-bundle-type") || "",
          potential: parseInt(el.getAttribute("data-fl-potential-first-order-unit-cents"), 10) || 0
        };
      });
    }

    /* PRIORITY 01 — a bundle in the cart already covers individual lines.

       ATTRIBUTION IS PER LINE. Coverage is resolved for each overlapping
       line against the bundle types actually present, and only the types
       that really cover something are reported. Unioning all coverage and
       then naming the first bundle would let the copy claim the Printable
       Bundle contains an eBook — which it does not. */
    function detectBundleOverlap(lines) {
      var bundles = lines.filter(function (i) { return i.kind === "bundle" && i.bundleType; });
      if (!bundles.length) return null;

      var presentTypes = unique(bundles.map(function (b) { return b.bundleType; }));
      var hits = [];
      var attributed = {};

      lines.forEach(function (i) {
        /* Bundle lines are never flagged against each other — there is no
           bundle-to-bundle overlap concept. */
        if (i.kind === "bundle" || !i.family) return;
        var coveringTypes = presentTypes.filter(function (t) {
          return (BUNDLE_COVERS[t] || []).indexOf(i.kind) !== -1;
        });
        if (!coveringTypes.length) return;
        i.coveredBy = coveringTypes;
        hits.push(i);
        coveringTypes.forEach(function (t) { attributed[t] = true; });
      });

      if (!hits.length) return null;

      return {
        state: "bundle-overlap",
        items: hits,
        bundleTypes: Object.keys(attributed).sort(),
        flagLabel: "Overlaps with Bundle"
      };
    }

    /* PRIORITY 02 — an eBook already contains its own practical tools.
       ALL matching families aggregate into ONE card, never one per family. */
    function detectPackOverlap(lines) {
      var families = [], seen = {}, items = [];
      lines.forEach(function (i) {
        if (i.kind !== "ebook" || !i.family || seen[i.family]) return;
        var packs = lines.filter(function (p) { return p.kind === "pack" && p.family === i.family; });
        if (!packs.length) return;
        seen[i.family] = true;
        families.push(i.family);
        lines.forEach(function (x) {
          if (x.family === i.family && (x.kind === "ebook" || x.kind === "pack") && items.indexOf(x) === -1) items.push(x);
        });
      });
      if (!families.length) return null;
      return { state: "pack-overlap", items: items, families: families, flagLabel: "Tools already included" };
    }

    /* PRIORITY 03 — PRICE-AWARE, and aware of the offer.
       The bundle may only be recommended when the customer's current eBook
       selection would cost more than the bundle EVEN IF they also received
       the first-order discount. Comparing against undiscounted prices would
       recommend the bundle in cases where staying put is actually cheaper.
       Quantity is deliberately ignored: repeating one eBook can never
       manufacture an opportunity. */
    function detectBundleOpportunity(lines, host) {
      if (!host) return null;
      var bundlePrice = parseInt(host.getAttribute("data-fl-eb-price"), 10) || 0;
      var bundleUrl = host.getAttribute("data-fl-eb-url") || "";
      if (!bundlePrice || !bundleUrl) return null;   /* fail safe, never guess */

      if (lines.some(function (i) { return i.kind === "bundle" && i.bundleType === "ebooks"; })) return null;

      var bestByFamily = {};
      lines.forEach(function (i) {
        if (i.kind !== "ebook" || !i.family || !i.potential) return;
        if (bestByFamily[i.family] === undefined || i.potential > bestByFamily[i.family]) {
          bestByFamily[i.family] = i.potential;
        }
      });

      var families = Object.keys(bestByFamily);
      if (families.length < 2) return null;

      var selection = families.reduce(function (sum, f) { return sum + bestByFamily[f]; }, 0);
      if (selection <= bundlePrice) return null;

      return { state: "bundle-opportunity", items: [], families: families, url: bundleUrl };
    }

    function evaluate() {
      var lines = readLines();
      var host = root.querySelector("[data-fl-smart-host]");
      return detectBundleOverlap(lines)
          || detectPackOverlap(lines)
          || detectBundleOpportunity(lines, host)
          || { state: "none", items: [] };
    }

    /* Signature describes ONLY the relationship that produced the guidance —
       never quantity, never unrelated lines. Pairs are de-duplicated, so even
       if the same product ever appeared as two separate cart lines the
       signature would not change and a dismissed warning could not revive.
       The covering bundle types are part of the bundle-overlap signature, so
       a materially different overlap still re-evaluates normally. */
    function signature(res) {
      if (!res || res.state === "none") return null;
      var pairs = unique((res.items || []).map(function (i) { return i.family + ":" + i.kind; })).sort().join(",");
      if (res.state === "bundle-overlap") return "bundle-overlap|" + (res.bundleTypes || []).join(",") + "|" + pairs;
      if (res.state === "pack-overlap") return "pack-overlap|" + unique(res.families || []).sort().join(",") + "|" + pairs;
      if (res.state === "bundle-opportunity") return "bundle-opportunity|" + unique(res.families || []).sort().join(",");
      return null;
    }

    function writeCopy(res) {
      var card = root.querySelector('[data-fl-smart="' + res.state + '"]');
      if (!card) return;
      var heading = card.querySelector("[data-fl-smart-heading]");
      var text = card.querySelector("[data-fl-smart-text]");

      if (res.state === "bundle-overlap" && text) {
        var byFamily = {};
        res.items.forEach(function (i) {
          byFamily[i.family] = byFamily[i.family] || { ebook: false, pack: false };
          if (i.kind === "ebook") byFamily[i.family].ebook = true;
          if (i.kind === "pack") byFamily[i.family].pack = true;
        });
        var phrases = Object.keys(byFamily).map(function (f) {
          var e = byFamily[f];
          if (e.ebook && e.pack) return "the " + titleCase(f) + " eBook and its practical tools";
          if (e.ebook) return "the " + titleCase(f) + " eBook";
          return "the " + titleCase(f) + " practical tools";
        });

        /* Name only the bundle types that genuinely cover something here. */
        var types = res.bundleTypes || [];
        var bundleName = types.length > 1
          ? "Bundles"
          : (types[0] === "printables" ? "Printable Bundle" : "eBook Bundle");
        var verb = types.length > 1 ? "already include" : "already includes";

        text.textContent = "Your " + bundleName + " " + verb + " " + humanList(phrases) +
          ". You may not need every item currently in your cart.";
      }

      if (res.state === "pack-overlap" && heading && text) {
        var fams = unique(res.families || []).map(titleCase).filter(Boolean);
        if (fams.length === 1) {
          heading.textContent = "Your " + fams[0] + " eBook already includes these practical tools.";
          text.textContent = "The " + fams[0] + " Practical Pack contains tools already included inside the " +
            fams[0] + " eBook. Keep both only if you want those tools as a separate printable pack.";
        } else {
          heading.textContent = "Some of your eBooks already include these practical tools.";
          text.textContent = "Your " + humanList(fams) + " Practical Packs contain tools already included inside " +
            "their corresponding eBooks. Keep both only if you want those tools as separate printable packs.";
        }
      }
    }

    /* Flags are always cleared first, so a line that is no longer part of the
       relationship can never stay flagged after a re-render or an error. */
    function applyFlags(res, on) {
      root.querySelectorAll(".item.is-flagged").forEach(function (el) { el.classList.remove("is-flagged"); });
      root.querySelectorAll("[data-fl-flag]").forEach(function (el) { el.hidden = true; el.textContent = ""; });

      reviewActive = !!on && !!res.items && res.items.length > 0;
      if (reviewActive) {
        res.items.forEach(function (i) {
          i.el.classList.add("is-flagged");
          var flag = i.el.querySelector("[data-fl-flag]");
          if (flag) { flag.textContent = res.flagLabel || "Content overlap"; flag.hidden = false; }
        });
      }
      root.querySelectorAll("[data-fl-smart-review]").forEach(function (btn) {
        btn.setAttribute("aria-pressed", reviewActive ? "true" : "false");
        btn.textContent = reviewActive ? "Hide overlapping items" : "Review overlapping items";
      });
    }

    function showCard(state) {
      root.querySelectorAll("[data-fl-smart]").forEach(function (card) {
        card.hidden = card.getAttribute("data-fl-smart") !== state;
      });
    }

    function syncSmart() {
      var auto = evaluate();
      var res = auto;
      if (smartDismissSig && smartDismissSig === signature(auto)) {
        res = { state: "none", items: [] };
      }

      var isOverlap = res.state === "bundle-overlap" || res.state === "pack-overlap";
      showCard(res.state);
      if (isOverlap) writeCopy(res);

      if (res.state === "bundle-opportunity" && res.url) {
        var cta = root.querySelector("[data-fl-smart-compare]");
        if (cta) cta.setAttribute("href", res.url);
      }

      applyFlags(res, reviewActive && isOverlap);
      smartCurrent = res;
    }

    function syncAll() {
      syncConsent(root);
      syncOffer(root);
      syncSmart();
    }

    /* ---- delegated actions ------------------------------------------ */

    function changeByKey(key, quantity, announcement) {
      if (!key) return;
      enqueue(function () {
        return request(root, "cart/change.js", { id: key, quantity: quantity }, announcement);
      });
    }

    function keyOf(el) {
      var host = el.closest("[data-fl-key]");
      return host ? host.getAttribute("data-fl-key") : "";
    }

    root.addEventListener("click", function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      var review = target.closest("[data-fl-smart-review]");
      if (review && root.contains(review)) {
        event.preventDefault();
        applyFlags(smartCurrent, review.getAttribute("aria-pressed") !== "true");
        return;
      }

      var keep = target.closest("[data-fl-smart-keep]");
      if (keep && root.contains(keep)) {
        event.preventDefault();
        smartDismissSig = signature(evaluate());
        reviewActive = false;
        syncSmart();
        return;
      }

      var step = target.closest("[data-fl-qty-step]");
      if (step && root.contains(step)) {
        event.preventDefault();
        var line = step.closest("[data-fl-key]");
        var input = line && line.querySelector("[data-fl-qty]");
        if (!input) return;
        var delta = parseInt(step.getAttribute("data-fl-qty-step"), 10) || 0;
        var next = Math.min(99, Math.max(0, (parseInt(input.value, 10) || 0) + delta));
        if (next === (parseInt(input.value, 10) || 0)) return;
        input.value = next;
        changeByKey(line.getAttribute("data-fl-key"), next,
          next === 0 ? "Item removed from your cart." : "Cart updated.");
        return;
      }

      var remove = target.closest("[data-fl-remove]");
      if (remove && root.contains(remove)) {
        event.preventDefault();
        changeByKey(keyOf(remove), 0, "Item removed from your cart.");
        return;
      }

      var clear = target.closest("[data-fl-clear]");
      if (clear && root.contains(clear)) {
        event.preventDefault();
        enqueue(function () {
          return request(root, "cart/clear.js", {}, "Your cart is now empty.");
        });
      }
    });

    root.addEventListener("change", function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      if (target.matches("[data-fl-qty]")) {
        var line = target.closest("[data-fl-key]");
        if (!line) return;
        var qty = Math.min(99, Math.max(0, parseInt(target.value, 10) || 0));
        target.value = qty;
        changeByKey(line.getAttribute("data-fl-key"), qty,
          qty === 0 ? "Item removed from your cart." : "Cart updated.");
        return;
      }

      if (target.matches("[data-fl-consent-input]")) syncConsent(root);
    });

    /* The section re-render replaces innerHTML, so every evaluation re-queries
       the DOM. Both the offer presentation and Smart Cart are re-synced here:
       fresh Liquid would otherwise restore the AVAILABLE pane while the
       attempt marker is still in the URL. Bound once, on document, from
       inside this closure. */
    document.addEventListener("fitlibrary:cart:updated", function () {
      if (!document.contains(root)) return;
      syncAll();
    });

    syncAll();
  }

  FL.register("Cart", function (scope) {
    var host = scope || document;
    if (host.matches && host.matches("[data-fl-cart-section]")) init(host);
    if (!host.querySelectorAll) return;
    host.querySelectorAll("[data-fl-cart-section]").forEach(init);
  });
})();
